// src/features/auth/hooks/useLogout.ts
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch } from '@/app/hooks';
import { clearAuth } from '../state/auth-state/authSlice';
import { clearCurrentTenant } from '../state/tenant-state/tenantSlice';
// Use the CSRF-aware client: /auth/logout is a cookie-authenticated, state-changing
// POST, so the gateway REQUIRES the session-bound X-CSRF-Token. The plain '@/lib/api'
// instance never attaches it — the request 403s at the gateway and the backend never
// revokes the session (so a refresh re-authenticates from the still-valid cookie).
import { apiClient } from '@/lib/apiClient';
import { getCsrf, fetchSessionOutcome } from '@/lib/session';
import { detachOnSignOut } from '@/features/shared/components/notifications/desktopNotifications';
import { invalidateEntitlements } from '@/features/entitlements/useEntitlements';

export function useLogout() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  // clearSession fully tears down the browser's authenticated state WITHOUT
  // navigating: backend session cookie (correct /api/v1 path), Redux auth +
  // tenant, and ALL persisted identity — crucially `persist:root`, without which
  // redux-persist rehydrates the old identity on the next page and silently signs
  // the user back in as whoever was logged in before. Callers that need to stay on
  // the current page (e.g. the password-reset token flow) use this; `logout`
  // layers a navigate on top.
  const clearSession = useCallback(async () => {
    // ⚠️ FIRST, while the session is still valid.
    //
    // A desktop subscription that outlives the session keeps notifying this
    // browser — on a shared machine that is one person's mentions arriving for
    // whoever signs in next. Removing it needs an authenticated call, so it has
    // to happen before the logout POST revokes the session.
    //
    // ⚠️ It lives HERE, in the shared teardown, because there are two logout
    // paths and putting it in the other one was not enough: the Header's
    // Logout menu calls useLogout, and a dev run showed the subscription row
    // surviving a real sign-out. Everything that tears down a session goes
    // through clearSession.
    //
    // Best effort and never allowed to block: a failed detach must not trap
    // somebody in a session they are trying to leave, and a row we fail to
    // remove is pruned on its first 410.
    await detachOnSignOut();

    try {
      // The CSRF token is bound to the current session and lives in memory. On a
      // fresh page load (e.g. arriving on the reset link in a new tab) it isn't
      // populated yet — fetch it from /auth/session first so the logout POST
      // carries a valid X-CSRF-Token and actually reaches the backend to revoke.
      if (!getCsrf()) {
        await fetchSessionOutcome();
      }
      await apiClient.post('/api/v1/auth/logout');
    } catch (error) {
      // Ignore backend errors — we still want to clear locally.
      console.log('Backend logout failed, continuing with local logout', error);
    }
    dispatch(clearAuth());
    dispatch(clearCurrentTenant());
    // The next person to sign in on this browser must not see this company's
    // products (the store listener in useEntitlements also clears on the
    // identity change; this makes the sign-out teardown explicit).
    invalidateEntitlements();
    for (const key of [
      'accessToken', 'refreshToken', 'persist:root', 'userId', 'userEmail',
      'userName', 'userRole', 'currentWorkspace', 'currentTenantId',
      'currentTenantSlug', 'currentTenantName',
      // legacy keys that might cause redirects
      'auth_token', 'user', 'access_token', 'refresh_token', 'user_data', 'tenant_data',
    ]) {
      localStorage.removeItem(key);
    }
  }, [dispatch]);

  const logout = useCallback(async () => {
    try {
      await clearSession();
    } finally {
      navigate('/workspace-entry');
    }
  }, [clearSession, navigate]);

  return { logout, clearSession };
}